import type { ReactElement } from 'react';
import { Children, cloneElement } from 'react';

/**
 * Adds additional props to a single React child element.
 *
 * This function takes a single React element and merges additional props with its existing props.
 * It's useful for enhancing child components with extra properties without modifying their original implementation.
 *
 * @template T The type of props accepted by the child component.
 *
 * @param {ReactElement<T>} children A single React element to which props will be added.
 * @param {Partial<T>} additionalProps An object containing the additional props to merge.
 *
 * @returns {ReactElement<T>} A new React element with the merged props.
 *
 * @example
 * interface MyComponentProps { label: string; onClick?: () => void; }
 * const enhanced = addPropsToChildren<MyComponentProps>(
 *   <MyComponent label="Click me" />,
 *   { onClick: () => console.log('Clicked!') }
 * );
 *
 * @remarks
 * - If a prop already exists on the child, it will be overwritten by the additional prop.
 */
export function addPropsToChildren<T>(
    children: ReactElement<T>,
    additionalProps: Partial<T>,
): ReactElement<T> {
    const child = Children.only(children);
    return cloneElement(child, additionalProps);
}
